import { Box } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi"
import ErrorDialog from "components/ErrorDialog"
import MultipleFilePicker from "components/MultipleFilePicker"
import { FieldArray, Formik, FormikProps, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import React, { useEffect, useRef, useState } from "react"
import { IDocumentFormValues } from "../../interface/folder.interface"
import FileFormList from "../FileFormList"
import { fileValidationSchema, folderValidationSchema } from "../folderValidationSchema"
import { useTranslation } from "react-i18next"

interface DocumentEventListenerProps {
  fileUpsertResult: IApiResult
}

interface FormikDocumentAttachments {
  attachments: DocumentAttachments[]
}

interface DocumentAttachments {
  fileId: string
  name: string
  id: number
}

const DocumentEventListener = ({ fileUpsertResult }: DocumentEventListenerProps) => {
  const { values, setFieldValue } = useFormikContext<FormikDocumentAttachments>()

  useEffect(() => {
    if (fileUpsertResult.status !== 2) return
    setFieldValue("attachments", [
      ...values.attachments,
      fileUpsertResult.data
    ])
  }, [fileUpsertResult])

  return <></>
}

const FileForm = (document: IDocumentFormValues) => {
  const [fileUploadResult, uploadFile] = useApi() as [IApiResult, IApiFunc]
  const [fileUpsertResult, upsertFile] = useApi() as [IApiResult, IApiFunc]
  const [fileUpdateResult, updateFile] = useApi() as [IApiResult, IApiFunc]
  const [deleteFileResult, deleteFile] = useApi() as [IApiResult, IApiFunc]
  const [editFile, setEditFile] = React.useState<null | any>(null)
  const [files, setFiles] = useState([])
  const formRef = useRef<FormikProps<any>>(null)
  const formRefAttachments = useRef<FormikProps<any>>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (files.length > 0) {
      files.map((file) => {
        return uploadFile.file(apiEndpoints.documents.uploadattachment, file)
      })
    }
  }, [files, uploadFile])

  useEffect(() => {
    if (fileUploadResult.status === 2) {
      let valuesObj = {
        ...fileUploadResult.data,
        documentId: document?.formValues?.id
      }

      upsertFile.post(apiEndpoints.documents.uploadattachmenttodocument, valuesObj)
    }
  }, [fileUploadResult])

  const saveFile = (valuesObj: any) => {
    if (editFile !== null)
      updateFile.put(apiEndpoints.documents.updateattachment, valuesObj?.attachments[editFile])
  }

  const deleteFileCallback = (deleteUrl: any, deleteModal: any) => {
    deleteFile?.del(deleteUrl.replace("{id}", deleteModal?.attachment?.id))
  }

  return (
    <>
      {fileUpsertResult.status === 3 ||
        (fileUploadResult.status === 3 && <ErrorDialog title={t("global.upload_failed", "Upload failed")} message="" />)}
      <Formik
        innerRef={formRef}
        initialValues={document.formValues}
        validationSchema={folderValidationSchema(t)}
        onSubmit={(e: any) => document?.handleSubmit(e)}
        enableReinitialize={true}>
        {({ handleSubmit }) => (
          <form id="CustomFieldForm" onSubmit={handleSubmit} noValidate>
            <Box marginTop={2} marginBottom={2}>
              <MultipleFilePicker
                isLoading={fileUpsertResult.status === 1 || fileUploadResult.status === 1}
                setFiles={setFiles}
              />
            </Box>
          </form>
        )}
      </Formik>
      <Formik
        ref={formRefAttachments}
        validationSchema={fileValidationSchema(t)}
        initialValues={document.formValues}
        onSubmit={saveFile}
        enableReinitialize={true}>
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form id="AttachmentForm" onSubmit={handleSubmit} noValidate>
            <DocumentEventListener fileUpsertResult={fileUpsertResult} />
            <FieldArray
              name="attachments"
              render={() => (
                <FileFormList
                  values={values}
                  deleteFile={deleteFile}
                  deleteFileResult={deleteFileResult}
                  deleteFileCallback={deleteFileCallback}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  editFile={editFile}
                  setEditFile={setEditFile}
                  fileUpdateResult={fileUpdateResult}
                  downloadUrl={apiEndpoints.documents.getattachment}
                  deleteUrl={apiEndpoints.documents.deleteattachment}
                />
              )}
            />
          </form>
        )}
      </Formik>
    </>
  )
}

export default FileForm
